import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { getMyDossiers } from "../../api/dossier/getMyDossiers";
import { qualifyNewLead } from "../../api/user/qualifyNewLead";
import PageContainer from "../../components/PageContainer";
import { isFFDriverPortalEnabled } from "../../helpers/configuration";
import {
  filterVisibleDossiers,
  hasOpenDossier,
  isDossierActivated,
} from "../../helpers/dossiers";
import { useAuth } from "../../providers/AuthProvider";
import DossierList from "../components/DossierList";

const DossierListPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const response = getMyDossiers();
  const configMap = getPublicConfig();
  const { getScopedPageUrl } = useAuth();
  const [navigationCheckComplete, setNavigationCheckComplete] = useState(false);
  const openFirstDossier =
    !!searchParams.get("openFirst") && searchParams.get("openFirst") === "true";

  if (response.isError) {
    throw response.error;
  }

  if (configMap.isError) {
    throw configMap.error;
  }

  const driverPortalEnabled = configMap.data
    ? isFFDriverPortalEnabled(configMap.data)
    : false;
  const { mutateAsync, isLoading: isUpdating } = qualifyNewLead();

  useEffect(() => {
    // If there is only one dossier then navigate to it
    if (response.isSuccess && !response.isFetching && configMap.isSuccess) {
      const dossiers = filterVisibleDossiers(response.data);
      const hasOnlyOneOpen =
        dossiers.length === 1 && dossiers[0].status === "New";
      const hasOnlyOneActivated =
        driverPortalEnabled &&
        dossiers.length === 1 &&
        isDossierActivated(dossiers[0]);

      if (
        dossiers.length > 0 &&
        (hasOnlyOneOpen || hasOnlyOneActivated || openFirstDossier)
      ) {
        const dossier = dossiers[0];
        const target = hasOnlyOneActivated ? "dossierActivated" : "viewDossier";

        navigate(`${getScopedPageUrl(target, dossier.id)}${location.search}`, {
          replace: true,
        });
      } else {
        setNavigationCheckComplete(true);
      }
    }
  }, [response.data, response.isFetching, configMap.isSuccess]);

  const onNewQuote = async () => {
    await mutateAsync();
    navigate(`${getScopedPageUrl("viewDossiers")}?openFirst=true`, {
      replace: true,
    });
  };

  const onSubmitClaim = async () => {
    navigate(
      `${getScopedPageUrl(
        "latestDossierRedirect",
        "createDossierExpenseClaim"
      )}?status=active`
    );
  };

  const isLoading =
    response.isFetching ||
    isUpdating ||
    !navigationCheckComplete ||
    configMap.isFetching;
  const isSuccess =
    response.isSuccess && navigationCheckComplete && configMap.isSuccess;

  return (
    <PageContainer loading={isLoading} title="Dossiers">
      {isSuccess && (
        <DossierList
          dossiers={response.data}
          onNewQuote={onNewQuote}
          onSubmitClaim={onSubmitClaim}
          canCreateDossier={!hasOpenDossier(response.data)}
          driverPortalEnabled={isFFDriverPortalEnabled(configMap.data)}
        />
      )}
    </PageContainer>
  );
};

export default DossierListPage;
